import type { DriverStatusChangeInput } from '@chukua/common';
import { EMPLOYMENT_STATUSES } from '@chukua/common';
import { Button, DatePicker, Form, Input, Modal, Select, Tag } from 'antd';
import dayjs, { type Dayjs } from 'dayjs';
import { useState } from 'react';
import type { DriverView } from './drivers.api';
import { EMPLOYMENT_STATUS_COLORS, EMPLOYMENT_STATUS_LABELS } from './employment-status-labels';

interface StatusFormValues {
  status: DriverView['employmentStatus'];
  effectiveAt: Dayjs;
  reason?: string;
}

interface DriverStatusControlProps {
  driver: Pick<DriverView, 'employmentStatus'>;
  canManage: boolean;
  onChangeStatus: (input: DriverStatusChangeInput) => Promise<boolean>;
}

/** Employment status is a free transition (active/suspended/left, any direction) with an effective date and an optional reason — not a single toggle like a customer's active flag. */
export function DriverStatusControl({ driver, canManage, onChangeStatus }: DriverStatusControlProps) {
  const [open, setOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [form] = Form.useForm<StatusFormValues>();

  const statusTag = <Tag color={EMPLOYMENT_STATUS_COLORS[driver.employmentStatus]}>{EMPLOYMENT_STATUS_LABELS[driver.employmentStatus]}</Tag>;

  if (!canManage) return statusTag;

  async function submit(values: StatusFormValues) {
    setSubmitting(true);
    try {
      const changed = await onChangeStatus({ status: values.status, effectiveAt: values.effectiveAt.format('YYYY-MM-DD'), reason: values.reason || undefined });
      if (changed) {
        form.resetFields();
        setOpen(false);
      }
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <>
      <div className="flex items-center gap-2">
        {statusTag}
        <Button size="small" onClick={() => setOpen(true)}>
          Change status
        </Button>
      </div>
      <Modal open={open} title="Change employment status" onCancel={() => setOpen(false)} footer={null} destroyOnClose>
        <Form<StatusFormValues>
          form={form}
          layout="vertical"
          requiredMark={false}
          onFinish={submit}
          disabled={submitting}
          initialValues={{ status: driver.employmentStatus, effectiveAt: dayjs() }}
        >
          <Form.Item name="status" label="Status" rules={[{ required: true }]}>
            <Select options={EMPLOYMENT_STATUSES.map((status) => ({ value: status, label: EMPLOYMENT_STATUS_LABELS[status] }))} />
          </Form.Item>
          <Form.Item name="effectiveAt" label="Effective date" rules={[{ required: true, message: 'Select an effective date' }]}>
            <DatePicker className="w-full" />
          </Form.Item>
          <Form.Item name="reason" label="Reason (optional)">
            <Input.TextArea rows={2} />
          </Form.Item>
          <Form.Item className="mb-0 flex justify-end">
            <Button type="primary" htmlType="submit" loading={submitting}>
              Save
            </Button>
          </Form.Item>
        </Form>
      </Modal>
    </>
  );
}
